/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import HeaderLayout from "@/components/common/CustomLayout/HeaderLayout";
import Schedule from '@/pages/Bookings/Edit/Schedule'
import Button from "@/components/common/Button";
import { useAppDispatch, RootState } from '@/store';
import { getBookingDetail, editBooking } from '@/store/booking-detail/action';
import { ReactComponent as VenueIcon } from '@/assets/icons/ri-map-pin-2-fill.svg'
import { ReactComponent as UserIcon } from '@/assets/icons/ri-user-heart-fill.svg'
import { BOOKING_STATUS } from '@/pages/Bookings/constant';
import { currencyFormat, convertToLocalTime } from '@/utils';
import 'react-datepicker/dist/react-datepicker.css';
import {
  BOOKING_EDIT_STATUS,
} from "@/pages/Bookings/Edit/constants";
import {GENERAL_STATUS_STYLE} from "@/constant/status";
import { PAYMENT_METHODS } from '@/pages/Invoice/constant';
import { ReactComponent as IcRescheduled } from "@/assets/icons/ri-reschedule.svg";

const scheduleData = (bookingDetails: {[key:string]: string | number}[]) => {
  const summary = {}
  bookingDetails.forEach(detail => {
    if (summary[convertToLocalTime(detail.date)]) {
      summary[convertToLocalTime(detail.date)].push(detail)
    } else {
      summary[convertToLocalTime(detail.date)] = [{ ...detail }]
    }
  })
  return summary
}

const getBookingMethod = (id) => {
  const bookingMethods = ['Walk In', 'Customer App', 'Call or Chat']
  return bookingMethods[id-1]
}

const BookingEdit = () => {
  const { id } = useParams()
  const bookingDetailState = useSelector((state: RootState) => state.bookingDetail.state)
  const [bookingDetail, setBookingDetail] = useState<BookingDetailState>(null)
  const [price, setPrice] = useState<{totalPrice: number, discountPrice?: number, finalPrice: number, taxAmount: number}>(null)
  const [courtCount, setCourtCount] = useState(0)
  const statusMethod = useForm()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selectedStatus = statusMethod.watch('status')

  useEffect(() => {
    const setBookingStatus = (booking: Booking) => {
      statusMethod.setValue('status', booking.status)
      statusMethod.setValue('paymentMethodId', booking.invoice.payment_method_id)
    }
    const fetchBookingDetail = async () => {
      await dispatch(getBookingDetail({ id, success: setBookingStatus }))
    }
    fetchBookingDetail()
  }, [id])

  useEffect(() => {
    setBookingDetail({...bookingDetailState})
    const totalPrice: number = bookingDetailState?.total_price || 0
    const finalPrice: number = bookingDetailState?.final_price || 0
    const discountPrice: number = bookingDetailState?.discount_amount || 0
    const taxAmount: number = bookingDetailState?.tax_amount || 0
    setPrice({
      totalPrice,
      finalPrice,
      discountPrice,
      taxAmount
    })
    const date = {}
    bookingDetailState?.booking_details?.forEach(booking => {
      if (date[convertToLocalTime(booking.date)]) {
        date[convertToLocalTime(booking.date)].add(booking.venue_court_id)
      } else {
        date[convertToLocalTime(booking.date)] = new Set().add(booking.venue_court_id)
      }
    })
    const bookedCourts = Object.values(date) || []
    // @ts-ignore
    setCourtCount(bookedCourts.reduce((sum, court) => sum + court.size, 0))
    statusMethod.setValue('status', bookingDetailState.status)
  }, [bookingDetailState])
  const selectedStatusIdx = BOOKING_EDIT_STATUS.indexOf(bookingDetailState.status)
  const rescheduleStatus = ["PAID", "UNPAID"];
  return (
    <div>
      <HeaderLayout
        title='Edit Booking'
        rightComponent={
          <div className="flex p-0 gap-4">
            <FormProvider {...statusMethod}>
              <Controller
                name='status'
                render={({ field }) =>
                  <select
                    value={selectedStatus}
                    placeholder='Select status'
                    {...field}
                    className={`color-select ${GENERAL_STATUS_STYLE[selectedStatus]}`}
                  >
                    {
                      BOOKING_EDIT_STATUS.map((i, idx) =>
                        <option
                          key={i}
                          value={i}
                          className={GENERAL_STATUS_STYLE[i]}
                          disabled={idx < selectedStatusIdx ||
                            ['REFUND',
                            'PARTIAL_REFUND'].indexOf(i) > - 1}
                        >
                          {i.replace('_', ' ')}
                        </option>
                      )
                    }
                  </select>
                }
              />
            </FormProvider>
            <Button
              type='submit'
              variant='primary'
              className='whitespace-nowrap flex gap-2 px-12 py-2 justify-center items-center text-jsm font-medium'
              form='bookingForm'
              onClick={() => {
                const paymentMethodId = parseInt(statusMethod.getValues('paymentMethodId'), 10)
                dispatch(
                  editBooking({id: bookingDetail.id, data: { status: `${statusMethod.getValues('status')}`, payment_method_id: paymentMethodId }})
                ).then((res: {[key: string]: any}) => {
                  if (!res.error) {
                    navigate('/dashboard/booking')
                  }
                })
              }}
            >
              Update
            </Button>
          </div>
          }
      >
        {bookingDetail && (<div className='flex flex-col px-4 pt-6'>
          <div className='flex p-0 gap-6'>
            <div className='flex flex-col w-full p-0 gap-6 drop-shadow bg-white'>
              <div className='flex px-7 pt-7 gap-2 rounded-t-sm'>
                <p className='font-bold text-lg'>Booking Detail</p>
                <div className='h-full border-l border-grey-line-subtle' />
                <p className='text-lg text-grey-dark'>ID {bookingDetail?.custom_id}</p>
                {bookingDetail.rescheduled_to && 
                  <p className="group text-red-primary mt-0.5 ml-1"  data-tooltip-target="tooltip-default">
                    <IcRescheduled width="20px" height="20px"/>
                  <div className="absolute text-xs hidden group-hover:block rounded text-white bg-red-primary px-2 py-1">
                    Rescheduled
                  </div>
                  </p>
                }
              </div>
              <div className='flex flex-col p-7 gap-6 rounded-b-sm'>
                <div className="flex p-0 w-full">
                  <div className="flex w-full items-center p-8 gap-8 border border-grey-line-subtle">
                    <div className="text-red-primary">
                      <VenueIcon/>
                    </div>
                    <div className="flex flex-col p-0 gap-1">
                      <p className="text-sm text-grey-dark">Venue</p>
                      <p className="font-medium text-sm">{bookingDetail?.venue?.name}</p>
                    </div>
                  </div>
                  {bookingDetail.customer && <div className='flex w-full items-center p-8 gap-8 border border-grey-line-subtle'>
                    <div className='text-red-primary'>
                      <UserIcon />
                    </div>
                    <div className='flex flex-col p-0 gap-1'>
                      <p
                        className='text-sm text-grey-dark'>{bookingDetail.customer?.name}</p>
                      <p className='font-medium text-sm'>{bookingDetail.customer?.phone_number}</p>
                    </div>
                  </div>}
                  <div className="flex w-full items-center p-8 gap-8 border border-grey-line-subtle">
                    <div className="flex flex-col p-0 gap-1">
                      <p className="text-sm text-grey-dark">Tipe Booking</p>
                      <p className={`font-bold text-sm ${
                      bookingDetail.type === 'REGULAR' ? 'text-green-success' : 'text-purple-secondary'}`}>{bookingDetail.type}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col w-full p-0 gap-6">
                  <div className="flex flex-col p-0 gap-4">
                    <div className="flex flex-rows">
                      <p className="font-medium w-full">Schedule</p>
                      <div className={`${!rescheduleStatus.includes(bookingDetail.status) || bookingDetail.rescheduled_to !== null ? 'text-grey-dark' : 'text-red-primary'} 
                      cursor-pointer underline`}
                         onClick={() => {
                          if (!rescheduleStatus.includes(bookingDetail.status) || bookingDetail.rescheduled_to !== null) return
                          navigate(`/dashboard/booking/${id}/reschedule`)
                         }}>
                          Reschedule
                      </div>
                    </div>
                    {bookingDetail.booking_details && <Schedule summary={scheduleData(bookingDetail.booking_details)}/>}
                  </div>
                  <div className="w-full border-b border-grey-line-subtle"/>
                </div>
                <div className="flex flex-col p-0 gap-6">
                  <div className="flex flex-col p-0 gap-4">
                    <p className="font-medium">Summary</p>
                    <div className="flex flex-col p-4 gap-2 border border-grey-line-subtle rounded">
                      <div className="flex justify-between py-2 w-full">
                        <p className="text-xs">Field Booking ({courtCount})</p>
                        <p className="font-medium text-xs">{currencyFormat(price.totalPrice)}</p>
                      </div>
                      { bookingDetail.rescheduled_from && 
                        <div className='flex w-full justify-between items-center py-2'>
                          <p className='text-xs'>Paid From <span className='text-red-primary cursor-pointer underline' onClick={() => navigate(`/dashboard/booking/${bookingDetail.rescheduled_from.from_booking_id}/edit`)}>
                              #{bookingDetail.rescheduled_from.from_custom_id}
                            </span>
                          </p>
                          <p className='font-medium text-xs'>- {currencyFormat(bookingDetail.rescheduled_from.paid_amount)}</p>
                        </div>
                      }
                      <div className="flex justify-between py-2 w-full">
                        <p className="text-xs">Voucher</p>
                        <p className="font-medium text-xs">- {currencyFormat(price.discountPrice)}</p>
                      </div>
                      <div className="flex justify-between py-2 w-full">
                        <p className="text-xs">Local Tax ({bookingDetail.venue.tax_fee})%</p>
                        <p className="font-medium text-xs">{currencyFormat(price.taxAmount)}</p>
                      </div>
                      { bookingDetail.rescheduled_from && 
                        <div className='flex w-full justify-between items-center py-2'>
                          <p className='text-xs'>Reschedule Fee</p>
                          <p className='font-medium text-xs'>{currencyFormat(bookingDetail.rescheduled_from.fee)}</p>
                        </div>
                        }
                      <div className='w-full border-b border-grey-line-subtle' />
                      <div className="flex justify-between py-2 w-full">
                        <p className="text-xs">Total Payment</p>
                        <p className="font-medium text-xs">{currencyFormat(price.finalPrice)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center p-0 gap-6">
                  <div className="flex flex-col p-0 gap-2">
                    <p className="font-medium">Invoice</p>
                    <div onClick={() => navigate(`/dashboard/invoices/${bookingDetail.invoice_id}`)}>
                      <p className="text-red-primary cursor-pointer">{bookingDetail.invoice?.custom_id}</p>
                    </div>
                  </div>
                  <div className={`flex justify-center items-center px-4 py-2 ${BOOKING_STATUS[bookingDetail.status]?.bgColor}`}>
                    <p className={`font-medium text-sm ${BOOKING_STATUS[bookingDetail.status]?.textColor}`}>{bookingDetail.status}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className='flex flex-col w-2/5 p-0 gap-6 '>
            <div className='flex flex-col drop-shadow bg-white h-fit'>
              <div className='flex px-7 pt-7 gap-2 rounded-t-sm'>
                <p className="font-bold text-lg">
                  Payment
                </p>
              </div>
              <div className='flex flex-col p-7 pt-4 gap-4 rounded-b-sm'>
                <div className='flex flex-col p-0'>
                  <div className='flex justify-between py-2'>
                    <p className="text-sm text-grey-dark">Payment Method</p>
                    <FormProvider {...statusMethod}>
                    <Controller
                      name='paymentMethodId'
                      render={({ field }) =>
                        <select
                          placeholder='Select Payment Method'
                          {...field}
                          className='color-select bg-green-subtle text-green-success'
                        >
                          {
                            PAYMENT_METHODS.map((i, idx) =>
                              <option
                                key={i}
                                value={idx}
                                disabled={ i.toLowerCase() === "midtrans" }
                              >
                                {i}
                              </option>
                            )
                          }
                        </select>
                      }
                    />
                    </FormProvider>
                  </div>
                </div>
              </div>
            </div>

            <div className='flex flex-col drop-shadow bg-white h-fit'>
              <div className='flex px-7 pt-7 gap-2 rounded-t-sm'>
                <p className="font-bold text-lg">
                  Meta Info
                </p>
              </div>
              <div className='flex flex-col p-7 pt-4 gap-4 rounded-b-sm'>
                <div className='flex flex-col p-0'>
                  <div className='flex justify-between py-2'>
                    <p className="text-sm text-grey-dark">Booking ID</p>
                    <p className="font-medium text-sm">
                      {bookingDetail.custom_id}
                    </p>
                  </div>
                  <div className='flex justify-between py-2'>
                    <p className="text-sm text-grey-dark">Booking Label</p>
                    <p className="font-medium text-sm">{bookingDetail.label || '-'}</p>
                  </div>
                  <div className='flex justify-between py-2'>
                    <p className="text-sm text-grey-dark">Booking Method</p>
                    <p className="font-medium text-sm text-right">{getBookingMethod(bookingDetail.booking_method_id)}</p>
                  </div>
                  <div className='flex justify-between py-2'>
                    <p className="text-sm text-grey-dark">Subscription</p>
                    {bookingDetail.subscription_id !== "" ? 
                      <a href={`/dashboard/subscriptions/edit/${bookingDetail.subscription_id}`} className='text-jsm text-red-primary underline text-right cursor-pointer'>
                        See details
                      </a> 
                    :
                      <p className="font-medium text-sm text-right" >-</p>
                    }
                  </div>
                  
                    <div className='flex justify-between py-2'>
                      <p className="text-sm text-grey-dark">Rescheduled To</p>
                      { bookingDetail.rescheduled_to ? 
                        <div className="font-medium text-sm text-red-primary cursor-pointer underline" 
                          onClick={() => navigate(`/dashboard/booking/${bookingDetail.rescheduled_to.to_booking_id}/edit`)}
                          >{bookingDetail.rescheduled_to.to_custom_id}
                        </div> : 
                        <div className="font-medium text-sm">-</div> }
                    </div> 
                  
                  <div className='flex justify-between py-2'>
                    <p className="text-sm text-grey-dark">Rescheduled From</p>
                    { bookingDetail.rescheduled_from ?
                      <div className="font-medium text-sm text-red-primary cursor-pointer underline" 
                        onClick={() => navigate(`/dashboard/booking/${bookingDetail.rescheduled_from.from_booking_id}/edit`)}
                        >{bookingDetail.rescheduled_from.from_custom_id}
                      </div> : 
                      <div className="font-medium text-sm">-</div> }
                  </div> 
                </div>
              </div>
            </div>
            
            <div className='flex flex-col drop-shadow bg-white h-fit'>
              <div className='flex px-7 pt-7 gap-2 rounded-t-sm'>
                <p className="font-bold text-lg">
                  Log Info
                </p>
              </div>
              <div className='flex flex-col p-7 pt-4 gap-4 rounded-b-sm'>
                <div className='flex flex-col p-0'>
                  <div className='flex justify-between py-2'>
                    <p className="text-sm text-grey-dark">Created By</p>
                    <div className='flex flex-col'>
                        <p className="font-medium text-sm text-right">
                        
                        { // eslint-disable-next-line no-nested-ternary
                          bookingDetail.created_by_staff !== undefined && bookingDetail.created_by_staff.name !== "" ? 
                          bookingDetail.created_by_staff.name :  (bookingDetail.booking_method_id === 2 ? 'Customer' : 'System') }</p>
                      <p className="font-medium text-sm text-right text-grey-dark">{`${new Date(convertToLocalTime(Number(bookingDetail.created_at))).toLocaleString('id-ID', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}`}</p>
                    </div>
                  </div>
                  <div className='flex justify-between py-2'>
                    <p className="text-sm text-grey-dark">Updated By</p>
                    <div className='flex flex-col'>
                      <p className="font-medium text-sm text-right">
                        { bookingDetail.updated_by_staff !== undefined && bookingDetail.updated_by_staff.name !== "" ? 
                          bookingDetail.updated_by_staff.name :  'System' }
                        </p>
                      <p className="font-medium text-sm text-right text-grey-dark">{`${new Date(convertToLocalTime(Number(bookingDetail.updated_at))).toLocaleString('id-ID', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}`}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>)}
      </HeaderLayout>
    </div>
  )
}

export default BookingEdit
